import { useMemo } from 'react'

import { ClientError, isForbidden, isNotFound } from '@tribeplatform/gql-client'
import {
  PermissionContext,
  Post,
  PostStatus,
} from '@tribeplatform/gql-client/types'
import { dayjs } from '@tribeplatform/react-components/common'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { PostCardLoading } from '@tribeplatform/react-components/LoadingStates'
import { useAuthMember, usePost } from '@tribeplatform/react-sdk/hooks'

import { PostsCardItem } from '../../PostsBlock/components/PostsCardItem'
import { useCustomFields } from '../../PostsBlock/hooks/useCustomFields'
import { convertToStatic } from '../../Views/utils'
import { staticFieldsFactory } from '../config'
import type { SinglePostBlockProps } from '../types'
import { PostNotFound } from './PostNotFound'

interface Props {
  postId: string
  fields: SinglePostBlockProps['fields']
  postTypeId: string
  postTypeSlug: string
  activeFieldId: string
  preview: boolean
}
export const PostCard = ({
  postId,
  fields,
  postTypeId,
  postTypeSlug,
  activeFieldId,
  preview,
}: Props) => {
  const { isModerator, data: authMember } = useAuthMember()
  const {
    data: fetchedPost,
    error: _error,
    isLoading,
  } = usePost({
    variables: { id: postId },
    fields: 'default',
    useQueryOptions: {
      refetchOnMount: 'always',
      retry: false,
      enabled: !!postId,
    },
  })

  const staticFields = useStaticIntl(staticFieldsFactory)
  const { customFields, postType } = useCustomFields({
    id: preview && !postId ? postTypeId : fetchedPost?.postTypeId,
    slug: preview && !postId ? postTypeSlug : fetchedPost?.postType?.slug,
  })
  const post = useMemo(() => {
    if (preview && !postId) {
      return {
        postTypeId,
        postType: { id: postTypeId, slug: postTypeSlug },
        status: PostStatus.PUBLISHED,
        owner: { member: authMember },
        primaryReactionType: postType?.primaryReactionType,
        relativeUrl: '/',
        space: {
          name: 'Space',
          slug: 'space',
        },
        authMemberProps: {
          context: PermissionContext.POST,
          canReact: true,
          permissions: [
            {
              name: 'addReaction',
              isAuthorized: { authorized: true },
              inputPermissions: [],
              outputPermissions: [],
            },
            {
              name: 'updatePost',
              isAuthorized: { authorized: true },
              inputPermissions: [],
              outputPermissions: [],
            },
          ],
        },
        publishedAt: dayjs().subtract(5, 'day').toDate(),
      } as Post
    }

    return fetchedPost
  }, [
    authMember,
    fetchedPost,
    postId,
    postType?.primaryReactionType,
    postTypeId,
    postTypeSlug,
    preview,
  ])
  const error = _error as ClientError

  if (!post && !isLoading && error) {
    if (isNotFound(error) || isForbidden(error)) {
      return <PostNotFound />
    }
  }

  if (
    post &&
    !isModerator &&
    post?.status !== PostStatus.PUBLISHED &&
    authMember?.id &&
    post?.owner?.member?.id !== authMember?.id
  ) {
    return <PostNotFound />
  }

  if (isLoading || !post) {
    return <PostCardLoading />
  }

  return (
    <PostsCardItem
      post={post}
      fields={convertToStatic(fields, staticFields, customFields ?? []).filter(
        field =>
          field.enabled &&
          !(field.styles && field.styles.indexOf('card') === -1),
      )}
      preview={preview || !postId}
      activeFieldId={activeFieldId}
      isSinglePost
    />
  )
}
